<template>
  <div :id="id">
    <div v-if="showCount"
         class="search-results__count-text">
      <template v-if="nameFilter">
        Found {{ resultCountString }} for "<span class="search-results__count">{{
          nameFilter
        }}</span>"
      </template>
      <template v-else>
        Found {{ resultCountString }}
      </template>
    </div>

    <transition-group-fade
      :key="nameFilter"
      :class="layoutClass"
      class="search-results"
      hide-leave-transition
      intersect>
      <div
        v-for="(result, index) in internalResults"
        :key="result.id"
        :class="columnsClass"
        :data-index="index"
        class="search-result">
        <component
          :is="resultTemplates[activeResultTemplate]"
          :key="result.id"
          v-bind="result"
          :layout="layout"></component>
      </div>
    </transition-group-fade>

    <span v-if="results == null || results.length === 0"
          class="no-results">
      No results found
    </span>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import TransitionGroupFade from "~/Components/TransitionGroupFade.vue";

export default {
  name: "SearchResults",
  components: {
    TransitionGroupFade,
  },
  props: {
    id: {
      type: String,
      default: "search-results",
    },
    columns: {
      type: Number,
      default: 3,
      required: false,
    },
    showCount: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      listener: null,
      resultCountString: null,
      internalResults: [],
      resultTemplates: {
        default: () => import( "~/Components/Search/Results/Default.vue" ),
        pressRelease: () => import( "~/Components/Search/Results/PressRelease.vue" ),
        pressArticle: () => import( "~/Components/Search/Results/PressArticle.vue" ),
        report: () => import( "~/Components/Search/Results/Report.vue" ),
        blogPost: () => import( "~/Components/Search/Results/BlogPost.vue" ),
        caseStudy: () => import( "~/Components/Search/Results/CaseStudy.vue" ),
        webinar: () => import( "~/Components/Search/Results/Webinar.vue" ),
        product: () => import( "~/Components/Search/Results/Product.vue" ),
        podcast: () => import( "~/Components/Search/Results/Podcast.vue" ),
      },
    };
  },
  computed: {
    nameFilter () {
      let result = null;

      const filter = this.filters.find( f => f.alias === "name" );
      if ( filter && filter.value && filter.value.length ) {
        result = filter.value;
      }

      return result;
    },
    columnCount () {
      let columnCount = 1;

      if ( this.columns > 0 ) {
        columnCount = this.columns;
      } else if ( this.stateColumnCount > 0 ) {
        columnCount = this.stateColumnCount;
      }

      return columnCount;
    },
    layoutClass () {
      return `layout-${this.layout}`;
    },
    columnsClass () {
      return `cols-${this.columnCount}`;
    },
    ...mapState( {
      configNode: state => state.search.configNode,
      filters: state => state.search.filters,
      results: state => state.search.results,
      layout: state => state.search.layout,
      stateColumnCount: state => state.search.columnCount,
      activeResultTemplate: state => state.search.activeResultTemplate,
      resultCount: state => state.search.resultCount,
    } ),
  },
  watch: {
    results: {
      immediate: true,
      handler () {
        this.resultCountString = this.getResultCountString();
        this.internalResults = this.results;
      },
    },
  },
  created () {
    this.listener = window.addEventListener( "popstate", () => {
      this.initialise();
    } );
  },
  beforeDestroy () {
    window.removeEventListener( "popstate", this.listener );
    this.listener = null;
  },
  methods: {
    getResultCountString () {
      let result = "0 results";

      if ( this.resultCount === 1 ) {
        result = "1 result";
      } else {
        result = `${this.resultCount} results`;
      }

      return result;
    },
    ...mapActions( {
      initialise: "initialise",
    } ),
  },
};
</script>

<style lang="less" scoped>
.search-results {
  box-sizing: border-box;
  margin-top: 1.25rem;
  margin-bottom: 0.625rem;
  display: flex;
  flex-wrap: wrap;

  .search-result {
    margin: 5px;
    box-sizing: border-box;
    transition: height 0.5s ease-out;
    display: flex;
  }

  &__count-text {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 4rem;
    color: #434343;
  }

  &__count {
    color: #d71920;
  }
}

.no-results {
  display: block;
  text-align: center;
  margin: 1.25rem 0;
  width: 100%;
}

.search-results.layout-list {
  flex-direction: column;

  .search-result {
    margin: 0;
  }
}

.search-results.layout-grid {
  .search-result {
    .loop(6);

    @media (max-width: 992px) {
      width: 100% !important;
      flex-basis: 100% !important;
    }
  }
}

.search-results.layout-row {
  .search-result {
    flex-basis: ~"calc(100% - 10px)";
  }
}

.generate-search-result( @perRow ) {
  @percentage: 100 / @perRow;

  flex-basis: ~"calc(@{percentage}% - 10px)";
}

.loop( @counter ) when ( @counter > 0 ) {
  .loop((@counter - 1)); // next iteration

  &.cols-@{counter} {
    .generate-search-result(@counter) // code for each iteration;
  }
}
</style>
